// Phase 1a Polish — Consolidated stable-inventory list.
//
// Route: /assets/stable
// Read-only table of all stable-category assets (tarps, cables, pelicans, starlinks, ...).
// Header has a type-filter dropdown and a free-text search. Column headers toggle sort.
// Click a row's display_code to drill into AssetDetail for full edit.

import React, { useEffect, useMemo, useState } from "react";
import { buttonClass } from '../components/ui/Button';
import { Link } from "react-router-dom";
import type { ColumnDef } from "@tanstack/react-table";
import { useApi } from "../apiHelpers";
import BackLink from "../components/BackLink";
import { DataTable } from "../components/table/DataTable";
import { useToast } from "../components/Toast/ToastContext";
import useAssetsStore from "../stores/useAssetsStore";
import { Asset } from "./types";
import { AssetStatusTag } from "./StatusTags";
import Chip from "../components/ui/Chip";
import SearchField from "../components/ui/SearchField";
import Select from "../components/ui/Select";
import { formatEventDate } from "../utils/date-utils";
import PageLayout from "../components/ui/PageLayout";
import EmptyState from "../components/ui/EmptyState";
import { Skeleton, SkeletonGroup } from "../components/ui/Skeleton";

/**
 * The row the table sees: an asset plus its type's display label.
 *
 * The label used to be looked up out of page state inside the Type cell, which
 * is the one thing a module-scope column cannot do. It is resolved ONCE in the
 * filter memo below instead, so every column here is pure over its row and this
 * page needs no `meta` at all.
 */
interface StableRow extends Asset {
	/** null when the type id resolves to nothing (the em-dash cell). */
	type_label: string | null;
}

// Module scope, per the DataTable contract: stable identity, no closures over
// page state. Every column sorts, exactly as every header did before — the
// accessorFn carries the sort-typed value and `undefined` (never '' and never
// null) for an empty, which is what puts empties last ascending instead of
// making the comparator inconsistent.
//
// Alignment and tabular figures come from `meta`; the vessel owns the td, so a
// per-row color rides a span inside the cell.
const STABLE_COLUMNS: ColumnDef<StableRow, any>[] = [
	{
		id: "display_code",
		header: "Asset",
		accessorFn: (a) => a.display_code,
		cell: ({ row }) => (
			<Link
				to={`/assets/${row.original.id}`}
				className="text-on-surface font-medium hover:text-primary font-mono"
			>
				{row.original.display_code}
			</Link>
		),
	},
	{
		id: "type",
		header: "Type",
		// `|| undefined`, not `??`: an empty label is as empty as a missing
		// one, and only undefined lands on the house empties-last behavior.
		accessorFn: (a) => a.type_label || undefined,
		meta: { cellClass: "text-on-surface" },
		cell: ({ row }) => row.original.type_label ?? "—",
	},
	{
		id: "operational_status",
		header: "Status",
		// The stored key, not the label: that is what the page sorted by
		// before, and the two orders differ (Out of Commission vs ooc).
		accessorFn: (a) => a.operational_status,
		meta: { align: "center" },
		cell: ({ row }) => (
			<AssetStatusTag status={row.original.operational_status} />
		),
	},
	{
		id: "location_bucket",
		header: "Location",
		accessorFn: (a) => a.location_bucket,
		meta: { cellClass: "text-on-surface-variant" },
		cell: ({ row }) => row.original.location_bucket,
	},
	{
		id: "barcode",
		header: "Barcode",
		// See the Type column: a blank string is an empty too, and an
		// accessorFn may return neither a blank nor null for one.
		accessorFn: (a) => a.barcode || undefined,
		meta: { cellClass: "text-on-surface-variant font-mono" },
		cell: ({ row }) =>
			row.original.barcode ?? (
				<span className="italic text-on-surface-disabled">unlinked</span>
			),
	},
	{
		id: "created_at",
		header: "Created",
		accessorFn: (a) => a.created_at,
		meta: { numeric: true, cellClass: "text-on-surface-variant whitespace-nowrap" },
		cell: ({ row }) => formatEventDate(row.original.created_at),
	},
];

const AssetsStableList: React.FC = () => {
	const api = useApi();
	const toast = useToast();
	const { types, ensureTypesLoaded } = useAssetsStore();

	const [typeFilter, setTypeFilter] = useState<"all" | number>("all");
	const [rows, setRows] = useState<Asset[]>([]);
	const [loading, setLoading] = useState(true);
	const [search, setSearch] = useState("");
	const [includeRetired, setIncludeRetired] = useState(false);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useEffect(() => {
		ensureTypesLoaded().catch(() =>
			toast.error("Failed to load asset types"),
		);
	}, []);

	const stableTypes = useMemo(
		() => types.filter((t) => t.category === "stable" && t.is_active),
		[types],
	);

	useEffect(() => {
		let cancelled = false;
		const run = async () => {
			setLoading(true);
			try {
				const params = new URLSearchParams();
				if (typeFilter !== "all")
					params.set("asset_type_id", String(typeFilter));
				params.set("is_internal_pool", "all");
				// Same rule as the type list: a sold unit is still a unit, and the landing
				// card's total counts it. Hiding it here made the two disagree.
				params.set("is_sold", "all");
				params.set("is_active", includeRetired ? "all" : "true");
				params.set("pageSize", "500");

				let data: Asset[] = [];
				if (typeFilter !== "all") {
					const r = await api.get(`/assets?${params.toString()}`);
					data = r.data;
				} else {
					const perType = await Promise.all(
						stableTypes.map(async (t) => {
							const p = new URLSearchParams(params);
							p.set("asset_type_id", String(t.id));
							const r = await api.get(`/assets?${p.toString()}`);
							return r.data;
						}),
					);
					data = perType.flat();
				}
				if (!cancelled) setRows(data);
			} catch {
				if (!cancelled) toast.error("Failed to load stable assets");
			} finally {
				if (!cancelled) setLoading(false);
			}
		};
		run();
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [typeFilter, stableTypes.length, includeRetired]);

	const typeById = useMemo(() => {
		const m = new Map<number, string>();
		types.forEach((t) => m.set(t.id, t.label));
		return m;
	}, [types]);

	// Search only: the shared table owns the sort now. Each row is decorated
	// with its type's label in the same pass, so the columns stay pure over the
	// row and this array is the one thing that changes identity — memoized, so
	// row identity holds across re-renders for the landing latch.
	const visible = useMemo<StableRow[]>(() => {
		const decorated = rows.map((a) => ({
			...a,
			type_label: typeById.get(a.asset_type_id) ?? null,
		}));
		const q = search.trim().toLowerCase();
		if (q === "") return decorated;
		return decorated.filter((a) =>
			[a.display_code, a.notes ?? "", a.barcode ?? "", a.type_label ?? ""]
				.join(" ")
				.toLowerCase()
				.includes(q),
		);
	}, [rows, search, typeById]);

	return (
		<PageLayout kind="workspace" className="pt-4 space-y-4">
			<div>
				<BackLink to="/assets" label="Assets" className="mb-2" />
				<div className="flex justify-between items-center flex-wrap gap-3">
					<h1 className="text-2xl font-bold text-primary">
						Stable
						{visible.length > 0 && (
							<span className="text-on-surface-variant text-base font-normal ml-2">
								({visible.length})
							</span>
						)}
					</h1>
					<Link
						to="/assets/new?category=stable"
						className={buttonClass('filled', 'md')}
					>
						+ New Item
					</Link>
				</div>
			</div>

			<div className="flex flex-wrap items-center gap-3">
				<SearchField
					value={search}
					onChange={setSearch}
					label="Search stable assets"
					placeholder="Search stable assets…"
					className="flex-1 min-w-[240px]"
				/>
				<Select
					label="Stable type"
					value={String(typeFilter)}
					onChange={(key) =>
						setTypeFilter(key === "all" ? "all" : parseInt(key, 10))
					}
					width="fit"
					options={[
						{ key: "all", label: "All Stable" },
						...stableTypes.map((t) => ({ key: String(t.id), label: t.label })),
					]}
				/>
				<Chip
					pressed={includeRetired}
					onClick={() => setIncludeRetired((v) => !v)}
				>
					Include retired
				</Chip>
			</div>

			{/* The vessel is the shared table's: shell, radius, scroll
			    container, header band and the face on every row. The page's
			    own box, its accent hairline and its row borders are gone
			    rather than passed through. */}
			<DataTable<StableRow>
				data={visible}
				columns={STABLE_COLUMNS}
				getRowId={(a) => a.id}
				sortable
				initialSorting={[{ id: "display_code", desc: false }]}
				loading={loading}
				loadingContent={
					<SkeletonGroup label="Loading stable assets" className="space-y-1 p-3">
						{[0, 1, 2, 3, 4].map((n) => <Skeleton key={n} className="h-8 w-full" />)}
					</SkeletonGroup>
				}
				empty={<EmptyState variant="area">No items match</EmptyState>}
			/>
		</PageLayout>
	);
};

export default AssetsStableList;
